import * as api from '../api.js';
import moment from 'moment';

import * as Phone from './phoneNumber.js';

import { isEnumMember, parseValue } from './types.js';

const { Primitive } = api.PrevisitFormTemplateFieldConfig;
const VALID_EMAIL_REGEX =
  /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;

export const EMPLOYMENT_STATUS_SHOW_EMPLOYER = [
  api.PracticePreference.EmploymentStatus.Employed,
  api.PracticePreference.EmploymentStatus.FullTime,
  api.PracticePreference.EmploymentStatus.PartTime,
  api.PracticePreference.EmploymentStatus.SelfEmployed,
];

export function validatePhoneNumber({
  phoneNumber,
  errorMessage,
}: {
  phoneNumber: string;
  errorMessage: string;
}) {
  const isValidPhone = Phone.isValid(phoneNumber);
  if (!isValidPhone) {
    throw new Error(errorMessage);
  }
}

function isValidEmail(email: string): boolean {
  return VALID_EMAIL_REGEX.test(email.toLowerCase());
}

export function validateFormFieldValue(
  formField: api.PrevisitFormField.PrevisitFormFieldWithConfig,
  enablePrevisitSubscriberInfoCollection: boolean,
  patientGuarantorDetailsEnabled: boolean,
  tHandler?: Function,
) {
  const t = tHandler ? tHandler : (text: string) => text;
  const {
    primitive,
    label,
    value,
    required,
    min,
    max,
    interval,
    characterLimit,
  } = formField;

  const characterLimitExceeded = t('characterLimitExceeded', {
    label,
    characterLimit,
  });
  const isRequiredValue = t('isRequiredValue', { label });
  const invalidAnswer = t('invalidAnswerForQuestion', { label });
  const invalidPhoneNumber = t('invalidPhoneNumber', { label });
  const pleaseCompleteQuestion = t('pleaseCompleteQuestion', { label });

  if (!value) {
    if (required) throw new Error(isRequiredValue);
    return;
  }

  if (
    api.PrevisitFormTemplateFieldConfig.CHARACTER_LIMIT_PRIMITIVES.includes(
      primitive,
    )
  ) {
    if (characterLimit && value.length > characterLimit) {
      throw new Error(characterLimitExceeded);
    }
  }

  switch (primitive) {
    case Primitive.PatientAllergies:
      const allergies = parseValue<api.Allergy.PrevisitAllergy[]>(value);
      if (!allergies) {
        throw new Error(invalidAnswer);
      }
      if (required && !allergies.length) {
        throw new Error(pleaseCompleteQuestion);
      }

      const allergyRequiredFields: (keyof api.Allergy.PrevisitAllergy)[] = [
        'id',
        'name',
        'reactions',
        'severity',
      ];
      const hasInvalidAllergy = allergies.some((allergy) => {
        return allergyRequiredFields.some((field) => !allergy[field]);
      });

      if (hasInvalidAllergy) {
        throw new Error(pleaseCompleteQuestion);
      }
      return;
    case Primitive.Date:
      if (!moment(value, 'MM/DD/YYYY', true).isValid()) {
        const invalidDate = t('invalidDate', { label });
        throw new Error(invalidDate);
      }
      return;
    case Primitive.Email:
    case Primitive.PatientEmail:
      if (!isValidEmail(value)) {
        const invalidEmail = t('invalidEmail');
        throw new Error(invalidEmail);
      }
      return;
    case Primitive.Integer:
      const numericValue = parseInt(value);
      if (isNaN(numericValue)) {
        const invalidNumericValue = t('invalidNumericValue', { label });
        throw new Error(invalidNumericValue);
      }
      const minValue = min || Number.MIN_SAFE_INTEGER;
      const maxValue = max || Number.MAX_SAFE_INTEGER;
      const intervalValue = interval || 1;
      if (numericValue < minValue) {
        const invalidMinValue = t('invalidMinValue', { label, minValue });
        throw new Error(invalidMinValue);
      }
      if (numericValue > maxValue) {
        const invalidMaxValue = t('invalidMaxValue', { label, maxValue });
        throw new Error(invalidMaxValue);
      }
      if (numericValue % intervalValue !== 0) {
        const invalidIntervalValue = t('invalidIntervalValue', {
          label,
          intervalValue,
        });
        throw new Error(invalidIntervalValue);
      }
      return;
    case Primitive.PatientRace:
    case Primitive.PatientEthnicity:
    case Primitive.PatientWrittenLanguages:
    case Primitive.MultiEnum:
    case Primitive.PatientMedicalHistoryFamily:
      if (required && value === '[]') {
        throw new Error(isRequiredValue);
      }
      return;
    case Primitive.PatientAlcoholUsage:
      const alcohol = parseValue<api.PatientSocialHistory.AlcoholUsage>(value);
      if (!alcohol) {
        throw new Error(invalidAnswer);
      }
      if (
        alcohol.concernsAtHome === undefined ||
        (![
          api.PatientSocialHistory.AlcoholUsageStatus.NeverUsed,
          api.PatientSocialHistory.AlcoholUsageStatus.PreferNotToAnswer,
          api.PatientSocialHistory.AlcoholUsageStatus.UnableToObtain,
        ].includes(alcohol.status) &&
          (!alcohol.frequency || !alcohol.quantity))
      ) {
        const valuesAreAllRequired = t('valuesAreAllRequired', { label });
        throw new Error(valuesAreAllRequired);
      }
      return;
    case Primitive.PatientCareProviders:
      const careProviders =
        parseValue<api.CareProvider.CareProviderWithAddress[]>(value);

      if (!careProviders) {
        throw new Error(invalidAnswer);
      }
      if (required && !careProviders.length) {
        throw new Error(pleaseCompleteQuestion);
      }

      const careProviderRequiredFields: (keyof api.CareProvider.CareProviderWithAddress)[] =
        ['id', 'displayName', 'firstName', 'lastName', 'address'];
      const hasInvalidCareProvider = careProviders.some((careProvider) => {
        return careProviderRequiredFields.some((field) => !careProvider[field]);
      });

      if (hasInvalidCareProvider) {
        throw new Error(pleaseCompleteQuestion);
      }
      return;
    case Primitive.PatientEmergencyContact:
    case Primitive.PatientRelativeEmergencyContact:
      const emergencyContact = parseValue<api.Previsit.EmergencyContact>(value);
      const requiredRelationshipField =
        primitive === Primitive.PatientEmergencyContact
          ? 'emergencyContactRelationship'
          : 'patientRelationshipToEmergencyContact';
      if (
        !emergencyContact ||
        !emergencyContact.emergencyContactFirstName ||
        !emergencyContact.emergencyContactLastName ||
        !emergencyContact.emergencyContactPhone ||
        !emergencyContact[requiredRelationshipField]
      ) {
        const missingEmergencyContact = t('missingEmergencyContact', { label });
        throw new Error(missingEmergencyContact);
      }

      validatePhoneNumber({
        phoneNumber: emergencyContact.emergencyContactPhone,
        errorMessage: invalidPhoneNumber,
      });

      return;
    case Primitive.PatientEmergencyContacts:
      if (!required && value === '[]') return;

      const activeEmergencyContacts = parseValue<
        api.Previsit.PatientEmergencyContact[]
      >(value)?.filter(
        (contact) =>
          contact?.status &&
          contact.status !== api.Previsit.ModificationStatus.Removed,
      );
      const missingEmergencyContact = t('missingEmergencyContact', {
        label,
      });

      if (!activeEmergencyContacts?.length)
        throw new Error(missingEmergencyContact);

      activeEmergencyContacts.forEach((emergencyContact) => {
        const contactNumber =
          emergencyContact.cellPhoneNumber || emergencyContact.homePhoneNumber;
        if (
          !emergencyContact.firstName ||
          !emergencyContact.lastName ||
          !emergencyContact.emergencyContactRelationshipToPatient ||
          !emergencyContact.status ||
          !(emergencyContact.rank >= 0) ||
          !contactNumber
        ) {
          throw new Error(missingEmergencyContact);
        }
        validatePhoneNumber({
          phoneNumber: contactNumber,
          errorMessage: invalidPhoneNumber,
        });
      });
      return;
    case Primitive.PatientEnvironment:
      const patientEnvironment =
        parseValue<api.PatientEnvironment.PatientEnvironment>(value);
      if (!patientEnvironment) {
        throw new Error(invalidAnswer);
      }

      const environmentRequiredFields = [
        'livesWith',
        'livingSituation',
        'familyFriendsAvailableToHelp',
      ];
      const environmentValuesMissing = environmentRequiredFields.some(
        (field) => {
          const fieldValue =
            patientEnvironment[
              field as keyof api.PatientEnvironment.PatientEnvironment
            ];
          return typeof fieldValue !== 'boolean' && !fieldValue;
        },
      );

      if (environmentValuesMissing) {
        throw new Error(pleaseCompleteQuestion);
      }
      return;
    case Primitive.PatientEnvironmentPediatric:
      const patientEnvironmentPediatric =
        parseValue<api.PatientEnvironment.PatientEnvironment>(value);
      if (!patientEnvironmentPediatric) {
        throw new Error(invalidAnswer);
      }

      const environmentPediatricRequiredFields = [
        'livesWith',
        'livingSituation',
        'familyFriendsAvailableToHelp',
        'parentsGuardianInfo',
        'siblingInfo',
        'parentsMaritalStatus',
      ];
      const environmentPediatricValuesMissing =
        environmentPediatricRequiredFields.some((field) => {
          const fieldValue =
            patientEnvironmentPediatric[
              field as keyof api.PatientEnvironment.PatientEnvironment
            ];
          return typeof fieldValue !== 'boolean' && !fieldValue;
        });

      if (environmentPediatricValuesMissing) {
        throw new Error(pleaseCompleteQuestion);
      }
      return;
    case Primitive.PatientInsurance:
    case Primitive.PatientInsuranceCardPhotoUpload:
    case Primitive.PatientInsuranceCardUpload:
      const insuranceFieldValue =
        parseValue<api.PrevisitPatientInsurance.PrevisitInsuranceFormFieldValue>(
          value,
        );

      if (!insuranceFieldValue) {
        throw new Error(invalidAnswer);
      }

      if (typeof insuranceFieldValue.payingWithInsurance !== 'boolean') {
        throw new Error(pleaseCompleteQuestion);
      }

      if (
        !enablePrevisitSubscriberInfoCollection ||
        primitive === Primitive.PatientInsuranceCardPhotoUpload ||
        !insuranceFieldValue.insuranceCoverages
      ) {
        return;
      }

      // TODO uncomment once we ingest subscribers again
      // for (const insuranceCoverage of insuranceFieldValue.insuranceCoverages) {
      //   if (
      //     !insuranceFieldValue.subscribers ||
      //     !_.some(
      //       insuranceFieldValue.subscribers,
      //       subscriber => insuranceCoverage.subscriberId === subscriber.id
      //     )
      //   ) {
      //     throw new Error(
      //       `Please complete question "${label}" insurance subscriber ${insuranceCoverage.primaryPolicyHolderMemberId} profile`
      //     );
      //   }
      // }
      return;
    case Primitive.PatientMedications:
      const medications =
        parseValue<api.Medication.PrevisitMedication[]>(value);

      if (!medications) {
        throw new Error(invalidAnswer);
      }
      if (required && !medications.length) {
        throw new Error(pleaseCompleteQuestion);
      }

      const medicationRequiredFields: (keyof api.Medication.PrevisitMedication)[] =
        ['id', 'name', 'status'];
      const hasInvalidMedication = medications.some((medication) => {
        return medicationRequiredFields.some((field) => !medication[field]);
      });

      if (hasInvalidMedication) {
        throw new Error(pleaseCompleteQuestion);
      }
      return;
    case Primitive.PatientNCPDPPharmacies:
    case Primitive.PatientPharmacies:
      const pharmacies = parseValue<api.Pharmacy.PrevisitPharmacy[]>(value);

      if (!pharmacies) {
        throw new Error(invalidAnswer);
      }
      if (required && !pharmacies.length) {
        throw new Error(pleaseCompleteQuestion);
      }

      const pharmacyRequiredFields: (keyof api.Pharmacy.PrevisitPharmacy)[] = [
        'id',
        'name',
        'address',
      ];
      const hasInvalidPharmacy = pharmacies.some((pharmacy) => {
        return pharmacyRequiredFields.some((field) => !pharmacy[field]);
      });

      if (hasInvalidPharmacy) {
        throw new Error(pleaseCompleteQuestion);
      }
      return;
    case Primitive.PatientGrossIncome:
      const grossIncomeValue =
        parseValue<
          Partial<api.PrevisitPatientGrossIncome.GrossIncomeFormFieldValue>
        >(value) ?? {};
      if (grossIncomeValue?.income === undefined) {
        const invalidGrossIncomeValue = t('invalidGrossIncomeValue');
        throw new Error(invalidGrossIncomeValue);
      }

      if (!grossIncomeValue?.incomePeriod) {
        const invalidGrossIncomePeriod = t('invalidGrossIncomePeriod');
        throw new Error(invalidGrossIncomePeriod);
      }
      return;
    case Primitive.PatientGuarantorVerification:
      const guarantorVerificationValue =
        parseValue<api.Guarantor.PrevisitGuarantorVerificationFormFieldValue>(
          value,
        );
      const guarantorToVerify = guarantorVerificationValue?.guarantors?.find(
        (guarantor) =>
          guarantor.id === guarantorVerificationValue.selectedGuarantorId,
      );

      if (
        guarantorToVerify?.relationshipToPatient ===
        api.Common.Relationship.Self
      ) {
        return;
      }

      const isSomeoneElseSelected =
        guarantorVerificationValue?.isSomeoneElse &&
        guarantorVerificationValue.selectedGuarantorId;
      if (!guarantorToVerify && !isSomeoneElseSelected) {
        const pleaseSelectAGuarantor = t('pleaseSelectAGuarantor');
        throw new Error(pleaseSelectAGuarantor);
      }

      if (
        guarantorVerificationValue?.isSomeoneElse &&
        !guarantorVerificationValue?.someoneElse
      ) {
        const pleaseEnterGuarantorName = t('pleaseEnterGuarantorName');
        throw new Error(pleaseEnterGuarantorName);
      }
      return;
    case Primitive.PatientGuarantors:
      const guarantorValue =
        parseValue<api.Guarantor.PrevisitGuarantorFormFieldValue>(value);
      const selectedGuarantor = guarantorValue?.guarantors?.find(
        (guarantor) => guarantor.id === guarantorValue.selectedGuarantorId,
      );
      if (
        selectedGuarantor?.relationshipToPatient ===
        api.Common.Relationship.Self
      ) {
        return;
      }
      if (
        !guarantorValue ||
        !guarantorValue.selectedGuarantorId ||
        !selectedGuarantor
      ) {
        const pleaseSelectAGuarantor = t('pleaseSelectAGuarantor');
        throw new Error(pleaseSelectAGuarantor);
      }
      if (
        patientGuarantorDetailsEnabled &&
        !selectedGuarantor.newlyAdded &&
        !selectedGuarantor.address
      ) {
        const pleaseValidateGuarantorAddress = t(
          'pleaseValidateGuarantorAddress',
        );
        throw new Error(pleaseValidateGuarantorAddress);
      }

      if (
        patientGuarantorDetailsEnabled &&
        !selectedGuarantor.newlyAdded &&
        !selectedGuarantor.relationshipToPatient
      ) {
        const pleaseSelectGuarantorRelationship = t(
          'pleaseSelectGuarantorRelationship',
        );
        throw new Error(pleaseSelectGuarantorRelationship);
      }

      if (
        patientGuarantorDetailsEnabled &&
        selectedGuarantor.newlyAdded &&
        (!selectedGuarantor.id ||
          !selectedGuarantor.firstName ||
          !selectedGuarantor.lastName ||
          !selectedGuarantor.relationshipToPatient ||
          !selectedGuarantor.sex ||
          !selectedGuarantor.dateOfBirth ||
          isNaN(new Date(selectedGuarantor.dateOfBirth).valueOf()) ||
          !selectedGuarantor.address ||
          !selectedGuarantor.phoneNumberHome ||
          !selectedGuarantor.emailAddress ||
          !selectedGuarantor.employmentStatus ||
          (EMPLOYMENT_STATUS_SHOW_EMPLOYER.includes(
            selectedGuarantor.employmentStatus,
          ) &&
            !selectedGuarantor.employer))
      ) {
        const someGuarantorInfoIsMissing = t('someGuarantorInfoIsMissing');
        throw new Error(someGuarantorInfoIsMissing);
      }
      return;
    case Primitive.PatientSexualHealth:
      const sexualHealth =
        parseValue<api.PatientSocialHistory.SexualHealth>(value);
      if (!sexualHealth) {
        throw new Error(invalidAnswer);
      }

      const sexualHealthRequiredFields = [
        'active',
        ...(sexualHealth.active ? ['orientation', 'contraceptives'] : []),
      ];
      const sexualHealthValuesMissing = sexualHealthRequiredFields.some(
        (field) => {
          const fieldValue =
            sexualHealth[field as keyof api.PatientSocialHistory.SexualHealth];
          return typeof fieldValue !== 'boolean' && !fieldValue;
        },
      );

      if (sexualHealthValuesMissing) {
        throw new Error(pleaseCompleteQuestion);
      }
      return;
    case Primitive.PatientSexualIdentity:
      if (!required) return;
      const sexualIdentity =
        parseValue<api.PatientSocialHistory.SexualIdentityFieldValue>(value);
      if (!sexualIdentity) {
        throw new Error(invalidAnswer);
      }
      const sexualIdentityRequiredFields = [
        'orientation',
        'pronouns',
        'identity',
        'sexAtBirth',
        ...(sexualIdentity.orientation ===
        api.PatientSocialHistory.SexualOrientation.Other
          ? ['orientationRaw']
          : []),
        ...(sexualIdentity.pronouns ===
        api.PatientSocialHistory.SexualPronoun.Other
          ? ['pronounsRaw']
          : []),
        ...(sexualIdentity.identity ===
        api.PatientSocialHistory.SexualIdentity.Other
          ? ['identityRaw']
          : []),
        ...(sexualIdentity.sexAtBirth ===
        api.PatientSocialHistory.SexAtBirth.Other
          ? ['sexAtBirthRaw']
          : []),
      ];
      const sexualIdentityValuesMissing = sexualIdentityRequiredFields.some(
        (field) =>
          !sexualIdentity[
            field as keyof api.PatientSocialHistory.SexualIdentityFieldValue
          ],
      );

      if (sexualIdentityValuesMissing) {
        throw new Error(pleaseCompleteQuestion);
      }
      return;
    case Primitive.PatientSexualOrientation:
      if (!required) return;
      const sexualOrientation =
        parseValue<api.PatientSocialHistory.SexualOrientationFieldValue>(value);
      if (!sexualOrientation) {
        throw new Error(invalidAnswer);
      }

      const sexualOrientationRequiredFields = [
        'orientation',
        ...(sexualOrientation.orientation ===
        api.PatientSocialHistory.SexualOrientation.Other
          ? ['orientationRaw']
          : []),
      ];

      const sexualOrientationValuesMissing =
        sexualOrientationRequiredFields.some(
          (field) =>
            !sexualOrientation[
              field as keyof api.PatientSocialHistory.SexualOrientationFieldValue
            ],
        );

      if (sexualOrientationValuesMissing) {
        throw new Error(pleaseCompleteQuestion);
      }
      return;
    case Primitive.PatientGenderIdentity:
      if (!required) return;
      const genderIdentity =
        parseValue<api.PatientSocialHistory.GenderIdentityFieldValue>(value);
      if (!genderIdentity) {
        throw new Error(invalidAnswer);
      }

      const genderIdentityRequiredFields = [
        'identity',
        ...(genderIdentity.identity ===
        api.PatientSocialHistory.SexualIdentity.Other
          ? ['identityRaw']
          : []),
      ];

      const genderIdentityValuesMissing = genderIdentityRequiredFields.some(
        (field) =>
          !genderIdentity[
            field as keyof api.PatientSocialHistory.GenderIdentityFieldValue
          ],
      );

      if (genderIdentityValuesMissing) {
        throw new Error(pleaseCompleteQuestion);
      }
      return;
    case Primitive.PatientSubstanceUsage:
      const substanceUsage =
        parseValue<api.SubstanceUsage.PrevisitSubstanceUsage>(value);
      if (!substanceUsage) {
        throw new Error(invalidAnswer);
      }
      const substanceUsageRequiredFields = [
        'status',
        'concernsAtHome',
        ...(![
          api.SubstanceUsage.Status.NeverUsed,
          api.SubstanceUsage.Status.PreferNotToAnswer,
          api.SubstanceUsage.Status.UnableToObtain,
        ].includes(substanceUsage.status)
          ? ['frequency', 'substances']
          : []),
      ];
      const substanceUsageValuesMissing = substanceUsageRequiredFields.some(
        (field) => {
          const fieldValue =
            substanceUsage[
              field as keyof api.SubstanceUsage.PrevisitSubstanceUsage
            ];
          return typeof fieldValue !== 'boolean' && !fieldValue;
        },
      );
      if (substanceUsageValuesMissing) {
        throw new Error(pleaseCompleteQuestion);
      }
      return;
    case Primitive.PatientSurgicalHistory:
      const procedures = parseValue<api.Procedure.PrevisitProcedure[]>(value);
      if (!procedures) {
        throw new Error(invalidAnswer);
      }
      if (required && !procedures.length) {
        throw new Error(pleaseCompleteQuestion);
      }

      const hasInvalidProcedure = procedures.some((procedure) => {
        return !procedure || (procedure.name === 'OTHER' && !procedure.nameRaw);
      });
      if (hasInvalidProcedure) {
        throw new Error(pleaseCompleteQuestion);
      }
      return;
    case Primitive.Phone:
    case Primitive.PatientCellPhone:
    case Primitive.PatientHomePhone:
    case Primitive.PatientPhone:
    case Primitive.PatientWorkPhone:
      if (!Phone.isValid(value)) {
        throw new Error(invalidPhoneNumber);
      }
      return;
    case Primitive.PatientPreferredLanguage:
      if (value && !isEnumMember(api.Language.LanguageWithOther, value)) {
        throw new Error(invalidAnswer);
      }
      return;
    case Primitive.PatientPreferredPronoun:
      if (value && !isEnumMember(api.Patient.Pronoun, value)) {
        throw new Error(invalidAnswer);
      }
      return;
    case Primitive.PatientTobaccoUsage:
      const tobaccoUsage =
        parseValue<api.PatientSocialHistory.TobaccoUsage>(value);
      if (!tobaccoUsage) {
        throw new Error(invalidAnswer);
      }
      if (
        tobaccoUsage.status !== api.PatientSocialHistory.TobaccoStatus.Never &&
        tobaccoUsage.status !==
          api.PatientSocialHistory.TobaccoStatus.Unknown &&
        (!tobaccoUsage.amount || !tobaccoUsage.yearsOfUsage)
      ) {
        const invalidAmountAndYearsOfUsage = t('invalidAmountAndYearsOfUsage', {
          label,
        });
        throw new Error(invalidAmountAndYearsOfUsage);
      }
      return;

    case Primitive.PatientBillingDetails:
      const billingValues =
        parseValue<api.Payment.PaymentFormFieldValue>(value);
      if (
        billingValues?.success ||
        billingValues?.skipped ||
        billingValues?.confirmedWithStatus !== undefined
      ) {
        return;
      }
      throw new Error(pleaseCompleteQuestion);

    case Primitive.PatientWrittenLanguages:
      const languages = parseValue<api.Language.Language[]>(value);
      if (!languages) {
        throw new Error(invalidAnswer);
      }
      if (required && !languages.length) {
        throw new Error(pleaseCompleteQuestion);
      }

      const validLanguages = languages.every((language) =>
        isEnumMember(
          { ...api.Language.Language, ...api.Language.LanguageOther },
          language,
        ),
      );
      if (!validLanguages) {
        throw new Error(invalidAnswer);
      }
      return;

    case Primitive.PatientSsn:
    case Primitive.PatientSsn4:
      if (!required) return;
      const ssnFilledValue = parseValue<{ status: string }>(value);
      if (ssnFilledValue?.status !== 'FILLED_OUT') {
        throw new Error(pleaseCompleteQuestion);
      }
      return;

    case Primitive.BodyDiagram:
      const selectedIds = parseValue<string[]>(value);
      if (required && !selectedIds?.length) {
        throw new Error(pleaseCompleteQuestion);
      }
      return;

    case Primitive.FreehandBodyDiagram:
      if (required && !value) throw new Error(pleaseCompleteQuestion);
      return;

    case Primitive.PatientEmployer:
      if (!required) return;
      const employer = parseValue<
        api.Employer.EmployerFormFieldValue & { employerPhone?: string }
      >(value);
      if (!employer) {
        throw new Error(invalidAnswer);
      }
      const employerRequiredFields: (keyof api.Employer.EmployerFormFieldValue)[] =
        ['employer', 'employerRaw'];
      const employerValuesMissing = employerRequiredFields.some((field) => {
        const fieldValue = employer[field];
        return typeof fieldValue !== 'boolean' && !fieldValue;
      });
      if (employerValuesMissing) {
        throw new Error(pleaseCompleteQuestion);
      }
      return;

    // TODO: Add validation for remaining primitives
    case Primitive.Address:
    case Primitive.AppointmentConfirmation:
    case Primitive.Boolean:
    case Primitive.ButtonLink:
    case Primitive.RelativeDate:
    case Primitive.DocumentSigner:
    case Primitive.Enum:
    case Primitive.Float:
    case Primitive.MultiString:
    case Primitive.PatientAlcoholUsageConcern:
    case Primitive.PatientAddressMailing:
    case Primitive.PatientAddressMailingSameAsPhysical:
    case Primitive.PatientAddressPhysical:
    case Primitive.PatientCareProvidersPicker:
    case Primitive.PatientDob:
    case Primitive.PatientEmploymentStatus:
    case Primitive.PatientExerciseRoutine:
    case Primitive.PatientFirstName:
    case Primitive.PatientLastName:
    case Primitive.PatientMaritalStatus:
    case Primitive.PatientMedicalHistoryPersonal:
    case Primitive.PatientPhoneSameAsCell:
    case Primitive.PatientPhotoId:
    case Primitive.PatientPhotoIdUpload:
    case Primitive.PatientPreferredFirstName:
    case Primitive.PatientPreferredPhone:
    case Primitive.PatientProblems:
    case Primitive.PatientReligion:
    case Primitive.PatientRetirementDate:
    case Primitive.PatientSex:
    case Primitive.PatientSubstanceUsageConcern:
    case Primitive.Registration:
    case Primitive.ScheduleAppointment:
    case Primitive.ScheduleLink:
    case Primitive.ScheduleSeriesAppointment:
    case Primitive.Static:
    case Primitive.String:
    case Primitive.TextInput:
    case Primitive.UsState:
    case Primitive.VirtualVisitInstructions:
    case Primitive.CustomContent:
      return;
    default:
      const throwMissingCase = (x: never): never => {
        throw new Error('Unreachable path, missing case');
      };
      throwMissingCase(primitive);
  }
}
